import { useMutation } from '@tanstack/react-query';
import {
	fetchAskNewEmailCode,
	fetchAskNewPhoneCode,
	fetchRegistrationEula,
	fetchSubmitClear,
	fetchSubmitEmailCode,
	fetchSubmitFirstStep,
	fetchSubmitLastStep,
	fetchSubmitPhoneCode,
	fetchSubmitPhoneVerifyCode
} from '../../api/registration';
import { TMappedCheckboxes } from '../../types/types';
import { ROUTES } from '../../types/routes';

export function useSubmitClearMutation(navigate: (path: string) => void) {
	const { mutateAsync: mutateClearAsync } = useMutation({
		mutationKey: ['submitClear'],
		mutationFn: fetchSubmitClear,
		onSuccess: async () => {
			localStorage.removeItem('state');
			navigate(
				(ROUTES.REGISTRATION.INDEX +
					'?returnHost=' +
					localStorage.getItem('returnHost')) as string
			);
			sessionStorage.removeItem('email');
			sessionStorage.removeItem('stateInfo');
			sessionStorage.removeItem('phone');
			sessionStorage.removeItem('phoneCode');
			sessionStorage.removeItem('countryCode');
			sessionStorage.removeItem('checkBoxes');
		}
	});
	return {
		mutateClearAsync
	};
}

export function useSelectAccountMutation(navigate: (path: string) => void) {
	const {
		mutateAsync,
		isError,
		error: mutationError,
		isPending
	} = useMutation({
		mutationKey: ['selectAccount'],
		mutationFn: (data: { accType: string }) => fetchRegistrationEula(data),
		onSuccess: async () => {
			navigate(ROUTES.REGISTRATION.PERSONAL);
		}
	});
	return {
		mutateAsync,
		isPending,
		mutationError,
		isError
	};
}

export function useSubmitFirstStepMutation(
	navigate: (path: string) => void,
	isEmailVerified: boolean
) {
	const {
		mutateAsync,
		isError,
		error: mutationError,
		isPending
	} = useMutation({
		mutationKey: ['submitFirstStep'],
		mutationFn: (data: {
			email: string;
			checkBoxes: TMappedCheckboxes;
			isEmailVerified: boolean;
		}) => fetchSubmitFirstStep(data),
		onSuccess: async () => {
			if (isEmailVerified) {
				navigate(ROUTES.REGISTRATION.PHONE);
				return;
			}
			navigate(ROUTES.REGISTRATION.EMAIL_CODE);
		}
	});
	return {
		mutateAsync,
		isPending,
		mutationError,
		isError
	};
}

export function useAskNewEmailCodeMutation() {
	const {
		mutateAsync: askNewCode,
		isError,
		error: askNewCodeError
	} = useMutation({
		mutationKey: ['askNewEmailCode'],
		mutationFn: fetchAskNewEmailCode
	});
	return {
		askNewCode,
		askNewCodeError,
		isError
	};
}

export function useSubmitEmailCodeMutation(navigate: (path: string) => void) {
	const {
		mutateAsync,
		isError,
		error: mutationError
	} = useMutation({
		mutationKey: ['submitEmailCode'],
		mutationFn: (data: { code: string }) => fetchSubmitEmailCode(data),
		onSuccess: async () => {
			navigate(ROUTES.REGISTRATION.PHONE);
		}
	});
	return {
		mutateAsync,
		mutationError,
		isError
	};
}

export function useSubmitPhoneVerifyMutation(navigate: (path: string) => void) {
	const {
		mutateAsync,
		isError,
		error: mutationError,
		isPending
	} = useMutation({
		mutationKey: ['submitPhoneVerifyCode'],
		mutationFn: (data: {
			phone: string;
			isPhoneVerified: boolean;
			countryCode: string;
		}) => fetchSubmitPhoneVerifyCode(data),
		onSuccess: async () => {
			navigate(ROUTES.REGISTRATION.PHONE_CODE);
		}
	});
	return {
		mutateAsync,
		mutationError,
		isError,
		isPending
	};
}

export function useAskNewPhoneCodeMutation() {
	const {
		mutateAsync: askNewCode,
		isError,
		error: askNewCodeError
	} = useMutation({
		mutationKey: ['askNewPhoneCode'],
		mutationFn: fetchAskNewPhoneCode
	});
	return {
		askNewCode,
		askNewCodeError,
		isError
	};
}

export function useSubmitPhoneCodeMutation(navigate: (path: string) => void) {
	const {
		mutateAsync,
		isError,
		error: mutationError
	} = useMutation({
		mutationKey: ['submitPhoneCode'],
		mutationFn: (data: { code: string }) => fetchSubmitPhoneCode(data),
		onSuccess: async () => {
			navigate(ROUTES.REGISTRATION.SUMMARY);
		}
	});
	return {
		mutateAsync,
		mutationError,
		isError
	};
}

export function useSubmitLastStepMutation(returnHost: string) {
	const {
		mutateAsync,
		isError,
		error: mutationError,
		isPending
	} = useMutation({
		mutationKey: ['submitPhoneCode'],
		mutationFn: (data: {
			name: string;
			password: string;
			referal: string;
		}) => fetchSubmitLastStep(data),
		onSuccess: async () => {
			sessionStorage.clear();
			if (window.top) {
				window.top.location.href = returnHost;
			}
			localStorage.clear();
		}
	});
	return {
		mutateAsync,
		mutationError,
		isError,
		isPending
	};
}
